import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../common/Header.js';
import Sidebar from '../common/Sidebar.js';
import Alert from '../common/Alert.js';
import { contactService } from '../../services/contactService.js';
import { Mail, Filter, Eye, Pencil, Trash } from "lucide-react";

const ContactList = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState('');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const perPage = 10;

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchContacts();
    }, 300);
    return () => clearTimeout(timer);
  }, [search, filter]);

  useEffect(() => {
    if (currentPage !== 1) {
      fetchContacts();
    }
  }, [currentPage]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (search) filters.search = search;
      if (filter) filters.replied = filter;
      const response = await contactService.getAllContacts(currentPage, perPage, filters);
      setContacts(response.data.data || []);
      setTotalPages(response.data.pagination.pages);
    } catch (err) {
      Alert.error('Failed to load contacts');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (contactId, contactName) => {
    const confirmed = await Alert.confirm(
      `Are you sure you want to delete the contact from "${contactName}"? This action cannot be undone.`,
      'Delete Contact'
    );
    if (confirmed) {
      try {
        await contactService.deleteContact(contactId);
        Alert.success('Contact deleted successfully!');
        if (contacts.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          fetchContacts();
        }
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to delete contact');
      }
    }
  };

  const toggleDropdown = (contactId) => {
    setActiveDropdown(activeDropdown === contactId ? null : contactId);
  };

  const clearFilters = () => {
    setSearch('');
    setFilter('');
    setCurrentPage(1);
  };

  const hasActiveFilters = search || filter;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="max-w-[1850px] mx-auto">
      <Header />
      <div className="flex">
        <Sidebar />
        <div className="flex bg-light w-full mt-16 sm:mt-0">
          <div className="flex-1 flex flex-col overflow-hidden">
            <main className="flex-1 overflow-auto p-3 sm:p-4 md:p-6">
              <div className="max-w-8xl mx-auto">
                {/* Page Header */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-4">
                    <div>
                      <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                        <Mail className="w-5 h-5 text-buttonBg" />
                        Contact Messages
                      </h1>
                      <p className="mt-1 text-sm text-gray-600">Manage customer inquiries</p>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white p-4 sm:p-6 rounded-sm shadow-sm mb-4 sm:mb-6">
                  <div className="flex items-center justify-between mb-0 sm:mb-4">
                    <div className="flex items-center gap-2">
                      <Filter className="w-4 h-4 sm:w-4 sm:h-4 text-buttonBg font-bold"/>
                      <h3 className="text-base sm:text-lg font-semibold text-tertiary">Filters</h3>
                    </div>
                  </div>
                  <div className='hidden sm:block'>
                    <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4'> 
                      {/* Search */}
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          placeholder="Search by name or email..."
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        />
                      </div>

                      {/* Mobile Filter Toggle */}
                      <button
                        onClick={() => setShowMobileFilters(!showMobileFilters)}
                        className="lg:hidden flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        <Filter size={18} />
                        Filters
                      </button>

                      {/* Desktop Filters */}
                      <div className="hidden lg:flex gap-3 flex-wrap">
                        <select
                          value={filter}
                          onChange={(e) => setFilter(e.target.value)}
                          className="px-4 py-2 w-full border border-gray-300 rounded-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        >
                          <option value="">All Status</option>
                          <option value="true">Replied</option>
                          <option value="false">Pending</option>
                        </select>

                        {hasActiveFilters && (
                          <button
                            onClick={clearFilters}
                            className="px-4 py-2 text-gray-600 hover:text-gray-800 underline text-sm"
                          >
                            Clear Filters
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Mobile Filters Dropdown */}
                  {showMobileFilters && (
                    <div className="lg:hidden mt-4 space-y-3 p-4 bg-gray-50 rounded-lg">
                      <select
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      >
                        <option value="">All Status</option>
                        <option value="true">Replied</option>
                        <option value="false">Pending</option>
                      </select>

                      {hasActiveFilters && (
                        <button
                          onClick={clearFilters}
                          className="w-full px-4 py-2 text-gray-600 hover:text-gray-800 underline text-sm"
                        >
                          Clear Filters
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="bg-white rounded-sm shadow-sm">
                  <div className="p-4 sm:p-6 lg:p-0">

                    {/* Table View - Desktop */}
                    <div className="hidden lg:block bg-white rounded-sm shadow-sm">
                      {loading ? (
                        <div className="p-12 text-center text-gray-600">
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
                          Loading contacts...
                        </div>
                      ) : contacts.length === 0 ? (
                        <div className="p-12 text-center text-gray-600">No contacts found</div>
                      ) : (
                        <>
                          <div className="hidden md:block w-full">
                                <table className="w-full table-fixed border-collapse">
                                  <thead>
                                    <tr className="bg-primary text-tertiary">
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {contacts.map((contact) => (
                                  <tr key={contact.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <button
                                        onClick={() => navigate(`/contact/view/${contact.id}`)}
                                        className="text-sm font-medium text-gray-900 hover:underline"
                                      >
                                        {contact.full_name}
                                      </button>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="text-sm text-gray-700 max-w-[180px] truncate">{contact.email}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="text-sm text-gray-700">{contact.phone}</div>
                                    </td>
                                    <td className="px-6 py-4">
                                      <div className="text-sm text-gray-700 max-w-xs truncate">{contact.subject}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                        contact.reply
                                          ? 'bg-green-100 text-green-800'
                                          : 'bg-yellow-100 text-yellow-800'
                                      }`}>
                                        {contact.reply ? 'Replied' : 'Pending'}
                                      </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="text-sm text-gray-700">{formatDate(contact.created_at)}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium relative">
                                      <button
                                        onClick={() => toggleDropdown(contact.id)}
                                        className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-md transition-colors"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                                          <circle cx="12" cy="5" r="2"/>
                                          <circle cx="12" cy="12" r="2"/>
                                          <circle cx="12" cy="19" r="2"/>
                                        </svg>
                                      </button>

                                      {activeDropdown === contact.id && (
                                        <div
                                          ref={dropdownRef}
                                          className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10"
                                        >
                                          <button
                                            onClick={() => { navigate(`/contact/view/${contact.id}`); setActiveDropdown(null); }}
                                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex gap-2 items-center border-b"
                                          >
                                            <Eye size={16} className="text-buttonBg" />
                                            <span className="text-base capitalize">View Details</span>
                                          </button>
                                          <button
                                            onClick={() => { navigate(`/contact/edit/${contact.id}`); setActiveDropdown(null); }}
                                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex gap-2 items-center border-b"
                                          >
                                            <Pencil size={16} className="text-buttonBg" />
                                            <span className="text-base capitalize">
                                              {contact.reply ? 'View Reply' : 'Reply'}
                                            </span>
                                          </button>
                                          <button
                                            onClick={() => { handleDelete(contact.id, contact.full_name); setActiveDropdown(null); }}
                                            className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 flex gap-2 items-center"
                                          >
                                            <Trash size={16} className="text-red-600" />
                                            <span className="text-base capitalize text-red-600">Delete</span>
                                          </button>
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          {/* Pagination */}
                          <div className="hidden md:flex bg-gray-50 px-6 py-4 justify-between items-center border-t">
                            <div className="text-sm text-gray-700">
                              Showing {contacts.length} contact{contacts.length !== 1 ? 's' : ''}
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition"
                              >
                                ← Previous
                              </button>
                              <span className="px-4 py-2 text-sm text-gray-700">
                                Page {currentPage} of {totalPages}
                              </span>
                              <button
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition"
                              >
                                Next →
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Card View - Mobile/Tablet */}
                    <div className="lg:hidden space-y-3 sm:space-y-4">
                      {loading ? (
                        <div className="bg-white p-8 rounded-lg shadow-sm text-center text-gray-600">
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
                          Loading contacts...
                        </div>
                      ) : contacts.length === 0 ? (
                        <div className="bg-white p-8 rounded-lg shadow-sm text-center text-gray-600">No contacts found</div>
                      ) : (
                        <>
                          {contacts.map((contact) => (
                            <div key={contact.id} className="bg-white rounded-lg shadow-sm p-4 sm:p-5">
                              <div className="flex items-start gap-3 sm:gap-4 mb-3">
                                <Mail className="w-10 h-10 text-tertiary flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <h3 className="font-semibold text-gray-900 text-base sm:text-lg truncate">
                                    {contact.full_name}
                                  </h3>
                                  <p className="text-xs sm:text-sm text-gray-600 truncate">{contact.email}</p>
                                  <span className={`mt-1 inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
                                    contact.reply
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-yellow-100 text-yellow-800'
                                  }`}>
                                    {contact.reply ? 'Replied' : 'Pending'}
                                  </span>
                                </div>
                              </div>

                              <div className="grid grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm mb-3">
                                <div>
                                  <span className="text-gray-500">Phone:</span>
                                  <p className="text-gray-900 font-medium">{contact.phone}</p>
                                </div>
                                <div>
                                  <span className="text-gray-500">Date:</span>
                                  <p className="text-gray-900 font-medium">{formatDate(contact.created_at)}</p>
                                </div>
                                <div className="col-span-2">
                                  <span className="text-gray-500">Subject:</span>
                                  <p className="text-gray-900 font-medium line-clamp-2">{contact.subject}</p>
                                </div>
                              </div>

                              <div className="flex gap-2">
                                <button
                                  onClick={() => navigate(`/contact/view/${contact.id}`)}
                                  className="flex-1 px-3 sm:px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-xs sm:text-sm font-medium"
                                >
                                  👁️ View
                                </button>
                                <button
                                  onClick={() => navigate(`/contact/edit/${contact.id}`)}
                                  className="flex-1 px-3 sm:px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-xs sm:text-sm font-medium"
                                >
                                  ✏️ {contact.reply ? 'View Reply' : 'Reply'}
                                </button>
                                <button
                                  onClick={() => handleDelete(contact.id, contact.full_name)}
                                  className="px-3 sm:px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors text-xs sm:text-sm font-medium"
                                >
                                  🗑️
                                </button>
                              </div>
                            </div>
                          ))}

                          {/* Mobile Pagination */}
                          <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm">
                            <div className="text-xs sm:text-sm text-gray-700 text-center mb-3">
                              Showing {contacts.length} contact{contacts.length !== 1 ? 's' : ''} - Page {currentPage} of {totalPages}
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className="flex-1 px-3 sm:px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition text-xs sm:text-sm font-medium"
                              >
                                ← Previous
                              </button>
                              <button
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                className="flex-1 px-3 sm:px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition text-xs sm:text-sm font-medium"
                              >
                                Next →
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactList;